import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { authConfig } from '../config/auth';
import { User } from '../utils/auth';
import { useBackdropDismiss } from '../hooks/useBackdropDismiss';
import '../styles/invite.css';

interface SupportModalProps {
  currentUser: User | null;
  onClose: () => void;
}

export default function SupportModal({ currentUser, onClose }: SupportModalProps) {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${authConfig.apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: currentUser?.firstName ?? '',
          lastName: currentUser?.lastName ?? '',
          companyName: currentUser?.companyName ?? currentUser?.companyId ?? '',
          email: currentUser?.email ?? '',
          message,
          source: 'in-app',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send message');
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const backdrop = useBackdropDismiss(onClose);

  if (success) {
    return (
      <div className="invite-modal-overlay" {...backdrop}>
        <div className="invite-modal" style={{ textAlign: 'center', padding: '40px 32px' }}>
          <CheckCircle2 size={52} color="#00bf62" style={{ marginBottom: 16 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: '#111827' }}>Message sent!</h2>
          <p style={{ color: '#6b7280', fontSize: 15, margin: '0 0 28px', lineHeight: 1.5 }}>
            We'll get back to you at <strong>{currentUser?.email}</strong> as soon as possible.
          </p>
          <button onClick={onClose} className="invite-submit-button" style={{ maxWidth: 180, margin: '0 auto', display: 'block' }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-modal-overlay" {...backdrop}>
      <div className="invite-modal">
        <h2 className="invite-modal-title">Get Help</h2>
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: -8, marginBottom: 20, lineHeight: 1.5 }}>
          Send us a message and we'll get back to you as soon as possible regarding your support request.
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="invite-input-group">
            <label className="invite-label">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="invite-input"
              required
              rows={5}
              placeholder="Tell us how we can help..."
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
          {error && <div className="invite-error">{error}</div>}
          <div className="invite-actions">
            <button type="button" onClick={onClose} className="invite-cancel-button" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="invite-submit-button" disabled={loading || !message.trim()}>
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
